import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone.js';
import utc from 'dayjs/plugin/utc.js';

import { isNumber, isString } from '@xemplo/common-util';
import { safeCall } from '@xemplo/safe-call';
import { Prettify } from '@xemplo/types';

import { DateTimeFormat, DayjsFormatMapper } from './date-time.const';
import {
  DateHelperProps,
  FalsyPolicy,
  FormattableValue,
  NormalizedDateHelperProps,
} from './date-time.types';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

/**
 * Default timezone identifier used when no
 * timezone is provided or fails to be resolved from browser (SSR)
 */
const DEFAULT_TZ = 'Australia/Sydney';

/**
 * Resolves the timezone every `DateTimeHelper` renders in by default — the browser's
 * zone, or `Australia/Sydney` when resolution fails (SSR).
 *
 * Export it so callers doing their own date maths (calendar-day diffs, `startOf('day')`)
 * can quantise in the *same* zone the helper formats in. Deriving the zone any other way
 * risks the two disagreeing on where a day boundary falls.
 */
export function resolveViewerTimezone() {
  const [tz, error] = safeCall(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  return error || tz == null ? DEFAULT_TZ : tz;
}

/**
 * Default configuration for the DateTimeHelper.
 * This provides sensible defaults for most cases and is cloned internally.
 */
const DEFAULT_CONFIG = {
  format: DateTimeFormat.Default,
  isUtc: false,
  fallback: '',
  falsyPolicy: FalsyPolicy.Fallback,
  tz: resolveViewerTimezone(),
} satisfies DateHelperProps;

/**
 * DateTimeHelper — A lightweight utility to assist with date formatting.
 * It's designed to provide the ergonomics for common use cases we have.
 *
 * It allows you to:
 *  - Reuse a default format configuration across multiple calls.
 *  - Apply per-call overrides for special cases.
 *  - Optionally use static formatting without creating an instance.
 *
 * Example usage:
 * ```ts
 * // Instance usage:
 * const helper = new DateTimeHelper({ format: DateTimeFormat.Default });
 * helper.format('2025-11-01');                                   // '01/11/2025'
 * helper.format('2025-11-01T15:42:00Z', { isUtc: true });        // '01/11/2025'
 * helper.format(new Date(), { format: DateTimeFormat.IsoDate }); // '2025-11-01'
 *
 * // Static usage:
 * DateTimeHelper.format('2025-11-01');          // '01/11/2025'
 * DateTimeHelper.format('2025-11-01T15:42:00Z', { isUtc: true }); // '01/11/2025'
 * DateTimeHelper.format(new Date(), { format: DateTimeFormat.IsoDate }); // '2025-11-01'
 *
 * DateTimeHelper.formatString('2025-11-01T15:42:00Z', { isUtc: true }); // '01/11/2025'
 * DateTimeHelper.formatString(new Date(), { format: DateTimeFormat.IsoDate }); // '2025-11-01'
 * DateTimeHelper.formatString(null); // ''
 * DateTimeHelper.formatString(undefined, { fallback: 'N/A' }); // 'N/A'
 * DateTimeHelper.formatString('invalid', { fallback: 'Whatever' }); // 'Whatever'
 * ```
 */
export class DateTimeHelper {
  private readonly _config: NormalizedDateHelperProps;

  /**
   * Creates a new instance of DateTimeHelper with an optional configuration.
   * @param config Optional configuration overrides for this helper instance.
   */
  constructor(config?: Partial<DateHelperProps>) {
    this._config = Object.freeze({ ...DEFAULT_CONFIG, ...config });
  }

  /**
   * Returns the configuration used by this instance.
   * @example
   * const cfg = new DateTimeHelper({ format: DateFormat.DateTime }).config;
   * console.log(cfg.format); // -> "DD/MM/YYYY HH:mm:ss A"
   */
  get config() {
    // This is a safe cast because we always assign all required properties in the constructor.
    // The freeze ensures immutability.
    // The use of Prettify ensures that the types are correctly inferred.
    return this._config as Prettify<Readonly<Required<DateHelperProps>>>;
  }

  private handleFalsy(policy: FalsyPolicy, fallback: string) {
    switch (policy) {
      case FalsyPolicy.Null:
        return null;
      case FalsyPolicy.Undefined:
        return undefined;
      case FalsyPolicy.Fallback:
      default:
        return fallback;
    }
  }

  /**
   * Formats a date using the instance defaults.
   * Supports one-off overrides via the second parameter.
   *
   * @param value The date value to format. Accepts `Date`, ISO string, number (timestamp), or `null`.
   * @param configOverrides Optional one-off overrides for format, fallback, or falsy policy.
   * @returns The formatted date string, `null`, `undefined`, or the fallback.
   * @remarks
   *  - This method respects the instance configuration unless overridden.
   *  - Returns `null` or `undefined` for falsy values based on the `falsyPolicy` setting.
   *  - If the date is invalid, it also follows the `falsyPolicy`.
   *  - the timezone (`tz`) is only consumed when `isUtc` is `true`.
   *
   * @example
   * const helper = new DateTimeHelper({ format: DateFormat.DateMonthShort });
   * helper.format('2025-11-01'); // "01 Nov"
   * helper.format(null, { falsyPolicy: 'empty' }); // ""
   */
  format(value?: FormattableValue, configOverrides?: Partial<DateHelperProps>) {
    const mergedConfig = { ...this._config, ...configOverrides };
    const { falsyPolicy, tz, isUtc, fallback, customInputFormat } = mergedConfig;

    if (!value) return this.handleFalsy(falsyPolicy, fallback);

    const [baseDate, error] = safeCall(() => {
      return isUtc
        ? dayjs.utc(value, customInputFormat).tz(tz)
        : dayjs(value, customInputFormat);
    });

    if (error || !baseDate?.isValid()) return this.handleFalsy(falsyPolicy, fallback);
    return baseDate.format(mergedConfig.format);
  }

  /**
   * Formats a date using the instance defaults.
   * Returns an empty string instead of `null` or `undefined` for falsy values.
   * This is a convenience method around `format` to prevent having to deal with falsy values
   * when you always want a string output.
   * This will force the `falsyPolicy` to `Fallback` internally even if you set a different one
   * in the instance or overrides.
   *
   * @param value The date value to format. Accepts `Date`, ISO string, number (timestamp), or `null`.
   * @param configOverrides Optional one-off overrides for format, fallback, or falsy policy.
   * @returns The formatted date string or the fallback.
   * @remarks
   * - This method respects the instance configuration unless overridden.
   * - Always returns a string, never `null` or `undefined`.
   * - If the date is invalid, it returns the fallback string.
   *
   * @example
   * const helper = new DateTimeHelper({ format: DateFormat.DateMonthShort });
   * helper.formatFallback('2025-11-01'); // "01 Nov"
   * helper.formatFallback(null); // ""
   */
  formatString(
    value?: FormattableValue,
    configOverrides?: Partial<DateHelperProps>,
  ): string {
    const mergedConfig = {
      ...this._config,
      ...configOverrides,
      falsyPolicy: FalsyPolicy.Fallback,
    };
    return this.format(value, mergedConfig) ?? mergedConfig.fallback;
  }

  /**
   * Formats multiple date values using the instance defaults.
   * This is a convenience method around `format` to process multiple values in one call.
   * Deliberately not supporting overrides per value to keep the API simple.
   *
   * @param args An array of date values to format.
   * @returns An array of formatted date strings, `null`, or `undefined` based on the instance configuration.
   * @example
   * const helper = new DateTimeHelper({ format: DateFormat.DateMonthShort, fallback: 'N/A' });
   * const many = helper.formatMany('2025-11-01', null, '2025-12-25');
   * // many = ['01 Nov', 'N/A', '25 Dec']
   *
   * const [dateFrom, dateTo] = helper.formatMany(api.dateFrom, api.dateTo);
   */
  formatMany(...args: Array<FormattableValue>): Array<string | null | undefined> {
    return args.map((value) => this.format(value));
  }

  /**
   * Static convenience method for quick one-off formatting
   * without creating a helper instance.
   *
   * @param value The date value to format.
   * @param config Optional configuration for this formatting call.
   * @returns The formatted date string, `null`, `undefined`, or the fallback.
   *
   * @example
   * DateTimeHelper.format('2025-11-01', { format: DateFormat.DateTime });
   */
  static format(
    value?: Date | string | number | null,
    config?: Partial<DateHelperProps>,
  ): string | null | undefined {
    return new DateTimeHelper().format(value, config);
  }

  /**
   * Formats a date value using a fallback configuration.
   *
   * If no value is provided, or if the value is invalid, the function applies a default formatting configuration.
   * The configuration can be partially overridden by passing a `config` object.
   *
   * @param value - The date value to format. Can be a `Date` object, a string, a number, or `null`/`undefined`.
   * @param config - Optional partial configuration to override default formatting options.
   * @returns The formatted date string.
   */
  static formatString(
    value?: Date | string | number | null,
    config?: Partial<DateHelperProps>,
  ): string {
    return new DateTimeHelper().formatString(value, config);
  }

  /**
   * Validates whether the given date string is a valid date
   * for the specified format.
   *
   * Uses strict parsing — the input must exactly match the provided format.
   * For the default AU format (`DD/MM/YYYY`), single-digit days and months
   * (e.g. `1/1/2025`) are also accepted via the `DayjsFormatMapper` relaxed format.
   *
   * @param date - The date value to validate. Accepts a string or `Date` object.
   * @param format - The expected `DateTimeFormat` of the input. Defaults to `DateTimeFormat.Default` (`DD/MM/YYYY`).
   * @returns `true` if the date is valid for the given format, `false` otherwise.
   *
   * @example
   * DateTimeHelper.isValidDate('01/11/2025');                              // true
   * DateTimeHelper.isValidDate('1/1/2025');                                // true  (D/M/YYYY)
   * DateTimeHelper.isValidDate('12/13/2026', DateTimeFormat.DefaultUSA);    // true (MM/DD/YYYY)
   * DateTimeHelper.isValidDate('1/13/2026', DateTimeFormat.DefaultUSA);      // true  (M/D/YYYY)
   * DateTimeHelper.isValidDate('not-a-date');                              // false
   * DateTimeHelper.isValidDate(null);                                      // false
   */
  static isValidDate(
    date?: string | null,
    format: DayjsFormatMapper = DateTimeFormat.Default,
  ) {
    if (!date) return false;
    const dayjsFormat = DayjsFormatMapper[format];
    return dayjs(date, [format, dayjsFormat], true).isValid();
  }

  /**
   * Validates an unknown date-like value.
   *
   * Validation flow:
   * 1. Rejects falsy values.
   * 2. Accepts valid `Date` objects.
   * 3. Rejects non-string and non-number inputs.
   * 4. Accepts values parseable by Day.js default parsing (e.g. ISO strings, timestamps).
   * 5. Falls back to strict validation against the provided app format.
   *
   * @param date The value to validate. Supports `Date`, `string`, or `number` (timestamp-like).
   * @param format The expected application date format for strict fallback validation.
   * @returns `true` when the input can be interpreted as a valid date, otherwise `false`.
   *
   * @example
   * DateTimeHelper.isValidDateValue(new Date()); // true
   * DateTimeHelper.isValidDateValue('2026-04-14T08:00:00Z'); // true
   * DateTimeHelper.isValidDateValue('14/04/2026', DateTimeFormat.Default); // true
   * DateTimeHelper.isValidDateValue({}); // false
   */
  static isValidDateValue(
    date?: unknown,
    format: DayjsFormatMapper = DateTimeFormat.Default,
  ) {
    if (!date) return false;
    // Already a date object
    if (date instanceof Date) return !isNaN(date.getTime());

    if (!isString(date) && !isNumber(date)) return false;
    // Standard ISO Format and Number handling
    if (dayjs(date).isValid()) return true;
    // Custom format
    return this.isValidDate(date.toString(), format);
  }

  /**
   * Static factory helper for ergonomic presets.
   * Useful when defining reusable formatters across your codebase.
   *
   * @param config Optional configuration for the helper instance.
   * @returns A new DateTimeHelper instance with the given config.
   *
   * @example
   * const MediumDate = DateTimeHelper.with({ format: DateFormat.DateMonthYearShort });
   * MediumDate.format('2025-11-01'); // "01 Nov 2025"
   */
  static with(config?: Partial<DateHelperProps>) {
    return new DateTimeHelper(config);
  }
}
